/**
 * 저장 클라이언트 — 프로젝트·워크플로·캔버스 버전·구성원. **웹판·스탠드얼론 공통** (백엔드 routers/store_api).
 *
 * 화면은 이 서버(BE1)만 부르고, 서버가 저장소(로컬 파일 / QBase 서비스 계정)를 고르며 프로젝트 권한을 판정한다
 * (docs/PROJECT_ACCESS.md). 웹판에서 서버가 아직 준비되지 않았으면(서비스 계정 미설정) repo.ts 가 예전 경로
 * (openflowRepo — 브라우저가 QBase 를 직접)로 떨어진다.
 *
 * saveCanvas 는 expectedCurVerNo 를 함께 보내 다른 세션이 먼저 저장했으면 409 로 거절된다(잃어버린 갱신 방지).
 */
import { Node, Edge } from '@xyflow/react';
import { migrateCanvas, stampSchema, toCanvasDoc, CanvasDoc } from './canvasSchema';
import { BE1_API } from '../config/api';
import type { Project, Workflow } from '../store/project/projectSlice';
import i18n from '../i18n';
import { errorText } from './errorText';
import { noteResponse as noteMaintenance } from './maintenance';

export type { CanvasDoc } from './canvasSchema';

async function call(path: string, init?: RequestInit): Promise<any> {
  const res = await fetch(`${BE1_API}${path}`, {
    credentials: 'include',
    ...init,
    headers: { ...(init?.body ? { 'Content-Type': 'application/json' } : {}), ...(init?.headers || {}) },
  });
  let body: any = null;
  try { body = await res.json(); } catch { /* 본문 없음 */ }
  // 큐베이스 점검 중 막힌 저장 — 띠가 알리고(services/maintenance) 문구는 서버 코드 qbase.maintenance 로. 다시 보내지 않는다
  noteMaintenance(res, body ?? undefined);
  if (!res.ok) throw new Error(errorText(body, `${res.status} ${res.statusText}`));
  return body;
}

// ---------------------------------------------------------------- 조회

export async function listProjects(): Promise<Project[]> {
  return ((await call('/api/store/projects')).items || []) as Project[];
}

export async function listWorkflows(projectId: string): Promise<Workflow[]> {
  const rows = (await call(`/api/store/workflows?projectId=${encodeURIComponent(projectId)}`)).items || [];
  // 로컬 저장소는 만든 때·바꾼 때를 regDt·updDt 로 싣는다
  return rows.map((r: any) => ({ ...r, createdAt: r.regDt || undefined, updatedAt: r.updDt || r.regDt || undefined })) as Workflow[];
}

/** 저장이 없던 워크플로(verNo<1)는 요청 없이 null */
export async function loadCanvas(workflowId: string, verNo: number): Promise<CanvasDoc | null> {
  if (!verNo || verNo < 1) return null;
  const res = await fetch(`${BE1_API}/api/store/workflows/${encodeURIComponent(workflowId)}/versions/${verNo}`, { credentials: 'include' });
  if (res.status === 404) return null;
  if (!res.ok) {
    let body: any;
    try { body = await res.clone().json(); } catch { body = undefined; }
    if (noteMaintenance(res, body)) throw new Error(errorText(body, `${res.status} ${res.statusText}`));
    throw new Error(i18n.t('common:error.canvasLoad', { status: res.status }));
  }
  noteMaintenance(res);
  const body = await res.json();
  return toCanvasDoc(migrateCanvas(body?.canvas));
}

export interface CanvasVersionInfo { verNo: number; regUsrid?: string; regDt?: string }

/** 버전 목록(최신순) — 서버가 파일 목록에서 조립해 준다 */
export async function listCanvasVersions(workflowId: string): Promise<CanvasVersionInfo[]> {
  return ((await call(`/api/store/workflows/${encodeURIComponent(workflowId)}/versions`)).items || []) as CanvasVersionInfo[];
}

// ---------------------------------------------------------------- 생성·수정

/** ownerUsrid 는 서버가 세션에서 정한다 — 시그니처 호환을 위해 인자는 유지 */
export async function createProject(input: { name: string; description?: string }, ownerUsrid: string): Promise<Project> {
  return (await call('/api/store/projects', {
    method: 'POST', body: JSON.stringify({ name: input.name, description: input.description ?? '' }),
  })) as Project;
}

export async function updateProject(p: Project): Promise<void> {
  await call(`/api/store/projects/${encodeURIComponent(p.id)}`, {
    method: 'PUT', body: JSON.stringify({ name: p.name, description: p.description ?? '', finalWorkflowId: (p as any).finalWorkflowId ?? null }),
  });
}

export async function createWorkflow(input: { projectId: string; name: string; description?: string; icon?: string; folder?: string }): Promise<Workflow> {
  return (await call('/api/store/workflows', {
    method: 'POST',
    body: JSON.stringify({ projectId: input.projectId, name: input.name, description: input.description ?? '', icon: input.icon ?? '📄', folder: input.folder ?? '' }),
  })) as Workflow;
}

export async function updateWorkflowMeta(w: { id: string; name: string; description?: string; folder?: string }): Promise<void> {
  await call(`/api/store/workflows/${encodeURIComponent(w.id)}`, {
    method: 'PUT', body: JSON.stringify({ name: w.name, description: w.description ?? '', ...(w.folder !== undefined ? { folder: w.folder } : {}) }),
  });
}

/** 소프트 삭제 — 버전 파일은 남는다 */
export async function retireWorkflow(workflowId: string): Promise<void> {
  await call(`/api/store/workflows/${encodeURIComponent(workflowId)}`, { method: 'DELETE' });
}

/** 새 캔버스 버전 저장 → 저장된 버전 번호 */
export async function saveCanvas(workflowId: string, curVerNo: number, doc: CanvasDoc, regUsrid: string): Promise<number> {
  const body = await call(`/api/store/workflows/${encodeURIComponent(workflowId)}/versions`, {
    method: 'POST',
    body: JSON.stringify({ canvas: stampSchema({ nodes: doc.nodes, edges: doc.edges, settings: doc.settings }), expectedCurVerNo: curVerNo || 0 }),
  });
  return Number(body?.verNo);
}


// ---------------------------------------------------------------- 구성원 (프로젝트 권한)

export type ProjectRole = 'admin' | 'owner' | 'editor' | 'viewer';
export interface ProjectMember { loginId: string; role: 'editor' | 'viewer' }

/** 프로젝트 구성원 — 소유자는 owner(프로젝트를 만든 사람), 나머지는 members. myRole 은 지금 사용자의 역할 */
export async function getProjectMembers(projectId: string): Promise<{ owner: string; members: ProjectMember[]; myRole: ProjectRole | null }> {
  const b = await call(`/api/store/projects/${encodeURIComponent(projectId)}/members`);
  return { owner: b.owner || '', members: b.members || [], myRole: b.myRole || null };
}

/** 구성원을 통째로 바꾼다 (소유자·관리자만) */
export async function setProjectMembers(projectId: string, members: ProjectMember[]): Promise<void> {
  await call(`/api/store/projects/${encodeURIComponent(projectId)}/members`, { method: 'PUT', body: JSON.stringify({ members }) });
}

/** 샘플 프로젝트를 최신(배포 샘플)으로 — 내 샘플은 backupName 으로 이름을 바꿔 보관하고 새로 복제한다 */
export async function refreshSample(backupName: string): Promise<{ project: Project; backups: { id: string; name: string }[] }> {
  return call('/api/store/sample/refresh', { method: 'POST', body: JSON.stringify({ backupName }) });
}

/** 서버가 이 저장 경로를 받을 준비가 됐는지 (웹판 서비스 계정 설정 여부) */
export async function storeReady(): Promise<boolean> {
  try {
    const b = await call('/api/store/info');
    return b?.ready === true;
  } catch {
    return false;
  }
}
